import { useEffect, useState } from 'react';
import { useTranslation } from '@/core';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/theme/components/ui/dialog';
import { Button } from '@/theme/components/ui/button';
import { Label } from '@/theme/components/ui/label';
import { Textarea } from '@/theme/components/ui/textarea';
import { useActiveContext } from '@/shared/hooks/useActiveContext';
import { useLocationCurrency } from '@/shared/hooks';
import { ChargeCatalogPicker } from '@/shared/components/ChargeCatalogPicker';
import { useChargeCatalog } from '@/modules/menus/hooks/useChargeCatalog';
import { sourceIdPayload, type ChargeCatalogItem } from '@/modules/menus/services/chargeCatalogService';
import { AuthorizeActionDialog } from '@/shared/components/AuthorizeAction';
import {
  readFolioCapRefusal,
  useCreateGuestChargeSellable,
  type FolioCapRefusal,
} from '../hooks/useGuestCharges';

interface ChargeSellableDialogProps {
  open: boolean;
  bookingId: string;
  onClose: () => void;
}

export function ChargeSellableDialog({ open, bookingId, onClose }: ChargeSellableDialogProps) {
  const { t } = useTranslation();
  const createMutation = useCreateGuestChargeSellable(bookingId);
  const { activeLocationId } = useActiveContext();
  const { formatPrice } = useLocationCurrency();

  const [selected, setSelected] = useState<ChargeCatalogItem | null>(null);
  const [quantity, setQuantity] = useState('1');
  const [description, setDescription] = useState('');
  // El tope de folio de la sede: el servidor contesta 409 cuando el cargo lo
  // pasa y la sede permite autorizarlo. El operador ya eligió el artículo, así
  // que no se vuelve a preguntar: se cambia de diálogo y se pide la firma.
  const [capRefusal, setCapRefusal] = useState<FolioCapRefusal | null>(null);

  // Chargeable items come from the location's active menus + services — not the
  // raw catalog. Guests always pay in real currency, so no token pricing here.
  const { data: catalog, isLoading } = useChargeCatalog(activeLocationId, false, { enabled: open });

  useEffect(() => {
    if (open) {
      setSelected(null);
      setQuantity('1');
      setDescription('');
      setCapRefusal(null);
    }
  }, [open]);

  const qty = parseFloat(quantity);
  const isValid = !!selected && !isNaN(qty) && qty > 0;

  const submit = async (auth?: {
    approverPassword?: string;
    approverNfcUid?: string;
    overrideReason?: string;
  }) => {
    if (!isValid || !selected) return;
    await createMutation.mutateAsync({
      bookingId,
      ...sourceIdPayload(selected),
      quantity: qty,
      description: description.trim() || undefined,
      ...auth,
    });
    onClose();
  };

  const handleSubmit = () => {
    void submit().catch((error: unknown) => {
      const refusal = readFolioCapRefusal(error);
      // Un tope que la sede NO deja saltar no se puede autorizar: lo cuenta el
      // toast del hook y el diálogo se queda como está.
      if (refusal?.kind === 'required') setCapRefusal(refusal);
    });
  };

  if (capRefusal) {
    return (
      <AuthorizeActionDialog
        open={open}
        onOpenChange={(o) => { if (!o) onClose(); }}
        requires="authorize"
        permissions={['location.authorize_folio_cap_override']}
        title={t('guestCharges.folioCap.title', 'Charge past the folio cap')}
        description={t(
          'guestCharges.folioCap.body',
          'This charge leaves the folio at {{total}}, above this location\u2019s cap of {{cap}}. A manager must authorize it.',
          {
            // Las cifras llegan como cadenas decimales del servidor; se
            // enseñan con la moneda y la precisión de la sede, no en crudo.
            total: formatPrice(Number(capRefusal.resultingTotal)),
            cap: formatPrice(Number(capRefusal.cap)),
          },
        )}
        confirmLabel={t('guestCharges.sellableDialog.confirm', 'Add charge')}
        onConfirm={(auth) => submit({
          approverPassword: auth.approverPassword,
          approverNfcUid: auth.approverNfcUid,
          // El motivo que pide el diálogo no se tira: va a
          // `FolioCharge.metadata`, junto a quién firmó.
          overrideReason: auth.reason,
        })}
      />
    );
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-serif text-[20px] font-semibold tracking-[-0.01em]">
            {t('guestCharges.sellableDialog.title', 'Add charge')}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {t('guestCharges.sellableDialog.description', 'Select an item and quantity to charge to the guest')}
          </DialogDescription>
        </DialogHeader>

        <div className="py-2">
          <ChargeCatalogPicker
            catalog={catalog}
            isLoading={isLoading}
            forStaff={false}
            selected={selected}
            onSelect={setSelected}
            quantity={quantity}
            onQuantityChange={setQuantity}
          />

          <div className="mt-3 space-y-2">
            <Label htmlFor="sell-description">
              {t('guestCharges.sellableDialog.descriptionLabel', 'Description (optional)')}
            </Label>
            <Textarea
              id="sell-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={createMutation.isPending}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={!isValid || createMutation.isPending}>
            {createMutation.isPending
              ? t('common.saving', 'Saving…')
              : t('guestCharges.sellableDialog.confirm', 'Add charge')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
